import { base64Decode, base64Encode } from "@openanalyst-ai/core/util/encode"
import type { AuthUser } from "@/context/auth"
import { pluginAppSessionHref } from "@/plugins/runtime"
import { normalizeOpenAnalystTenxBaseUrl, openAnalystTenxBaseUrl } from "./openanalyst-hosts"

type SmartworkProviderModel = {
  id: string
  name?: string
  status?: string
  cost?: unknown
  capabilities?: unknown
  variants?: unknown
}

type SmartworkProvider = {
  id: string
  name?: string
  models: Record<string, SmartworkProviderModel>
}

export type SmartworkOpenAnalystSession = {
  id: string
  title?: string | null
  slug?: string | null
  parentID?: string | null
  time?: {
    updated?: number | null
    created?: number | null
  }
  directory?: string | null
}

export type SmartworkOpenAnalystProject = {
  slug: string
  directory: string
  name?: string
  returnPath?: string
  sessionId?: string
  sessions?: SmartworkOpenAnalystSession[]
}

type SmartworkOpenAnalystSessionPayload = {
  id: string
  title?: string
  slug?: string
  parentID?: string
  time?: {
    updated?: number
    created?: number
  }
  directory?: string
}

export type SmartworkOpenAnalystModel = {
  providerID: string
  modelID: string
  variant?: string | null
  label?: string
  providerLabel?: string
}

export type SmartworkOpenAnalystProviders = {
  all: SmartworkProvider[]
  connected: string[]
  default: Record<string, string>
}

export type SmartworkOpenAnalystWorkspace = {
  schemaVersion: 1
  displayIdentity: {
    title: string
    namespace?: string
    subtitle?: string
    logoUrl?: string
    color?: string
    source: "cloud" | "local"
  }
  operationalIdentity: {
    desktopId?: string
    smartworkWorkspaceId?: string
    localRuntimeWorkspaceId?: string
    openAnalystProjectSlug?: string
    openAnalystProjectDirectory?: string
    openAnalystProjectId?: string
  }
  cloud?: {
    handle?: string
    handleDisplay?: string
    projectTitle?: string
    updatedAt?: string
    capabilityKeys?: string[]
  }
  activity?: {
    latestKind?: "agent" | "automation" | "message" | "runtime"
    label?: string
    detail?: string
    updatedAt?: string
  }
}

export const SMARTWORK_DEFAULT_APP_URL = "http://127.0.0.1:5173"
export const SMARTWORK_DEV_API_BASE_URL = "https://ai.openanalyst.com"
export const SMARTWORK_PRODUCTION_API_BASE_URL = "https://ai.openanalyst.com"
export const SMARTWORK_AUTH_MESSAGE_TYPE = "openanalyst:smartwork-auth"
export const SMARTWORK_LOGOUT_MESSAGE_TYPE = "openanalyst:smartwork-logout"
export const SMARTWORK_READY_MESSAGE_TYPE = "openanalyst:smartwork-ready"
export const SMARTWORK_PICK_DIRECTORY_REQUEST_MESSAGE_TYPE = "openanalyst:smartwork-pick-directory"
export const SMARTWORK_PICK_DIRECTORY_RESPONSE_MESSAGE_TYPE = "openanalyst:smartwork-pick-directory-response"
export const SMARTWORK_RETURN_PATH_STORAGE_KEY = "openanalyst.smartwork.returnPath.v1"

type Env = Record<string, string | boolean | undefined>
export type SmartworkView = "session" | "integrations" | "skills" | "plugins" | "automations" | "apps" | "billing"

type SmartworkStorage = Pick<Storage, "getItem" | "setItem">

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function currentEnv(): Env {
  return ((import.meta as ImportMeta & { env?: Env }).env ?? {}) as Env
}

function envString(env: Env, key: string) {
  const value = env[key]
  return typeof value === "string" ? value.trim() : ""
}

function smartworkBaseUrl(value: string) {
  return normalizeOpenAnalystTenxBaseUrl(value)
}

function httpBaseUrl(value: string | undefined) {
  const trimmed = value?.trim() ?? ""
  if (!trimmed) return undefined
  try {
    const url = new URL(trimmed)
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined
    return trimmed.replace(/\/+$/, "")
  } catch {
    return undefined
  }
}

function stringValue(value: string | undefined) {
  return value?.trim() || undefined
}

function optionalStringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function optionalNumberValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function normalizeStringList(value: unknown) {
  if (!Array.isArray(value)) return undefined
  const items = [...new Set(value.map(optionalStringValue).filter((item): item is string => !!item))]
  return items.length ? items : undefined
}

function jsonSafeObject(value: unknown) {
  if (!value || typeof value !== "object") return undefined
  try {
    return JSON.parse(JSON.stringify(value)) as unknown
  } catch {
    return undefined
  }
}

function providerModel(value: unknown): SmartworkProviderModel | null {
  if (!record(value)) return null
  const id = optionalStringValue(value.id)
  if (!id) return null
  const cost = jsonSafeObject(value.cost)
  const capabilities = jsonSafeObject(value.capabilities)
  const variants = jsonSafeObject(value.variants)
  return {
    id,
    ...(optionalStringValue(value.name) ? { name: optionalStringValue(value.name) } : {}),
    ...(optionalStringValue(value.status) ? { status: optionalStringValue(value.status) } : {}),
    ...(cost ? { cost } : {}),
    ...(capabilities ? { capabilities } : {}),
    ...(variants ? { variants } : {}),
  }
}

function provider(value: unknown): SmartworkProvider | null {
  if (!record(value)) return null
  const id = optionalStringValue(value.id)
  if (!id || !record(value.models)) return null
  const models = Object.fromEntries(
    Object.entries(value.models)
      .map(
        ([key, item]) =>
          [
            key,
            providerModel({
              ...(record(item) ? item : {}),
              id: record(item) && typeof item.id === "string" ? item.id : key,
            }),
          ] as const,
      )
      .filter((entry): entry is readonly [string, SmartworkProviderModel] => !!entry[1]),
  )
  if (Object.keys(models).length === 0) return null
  return {
    id,
    ...(optionalStringValue(value.name) ? { name: optionalStringValue(value.name) } : {}),
    models,
  }
}

function openAnalystSession(
  value: SmartworkOpenAnalystSession | undefined,
): SmartworkOpenAnalystSessionPayload | undefined {
  const id = stringValue(value?.id)
  if (!id) return undefined
  const updated = optionalNumberValue(value?.time?.updated)
  const created = optionalNumberValue(value?.time?.created)
  const time = updated !== undefined || created !== undefined
    ? {
        ...(updated !== undefined ? { updated } : {}),
        ...(created !== undefined ? { created } : {}),
      }
    : undefined
  return {
    id,
    ...(stringValue(value?.title ?? undefined) ? { title: stringValue(value?.title ?? undefined) } : {}),
    ...(stringValue(value?.slug ?? undefined) ? { slug: stringValue(value?.slug ?? undefined) } : {}),
    ...(stringValue(value?.parentID ?? undefined) ? { parentID: stringValue(value?.parentID ?? undefined) } : {}),
    ...(time ? { time } : {}),
    ...(stringValue(value?.directory ?? undefined) ? { directory: stringValue(value?.directory ?? undefined) } : {}),
  }
}

function openAnalystProject(value: SmartworkOpenAnalystProject | undefined) {
  const slug = stringValue(value?.slug)
  const directory = stringValue(value?.directory)
  if (!slug || !directory) return undefined
  const sessions = (value?.sessions ?? [])
    .map(openAnalystSession)
    .filter((item): item is SmartworkOpenAnalystSessionPayload => !!item)
  return {
    slug,
    directory,
    ...(stringValue(value?.name) ? { name: stringValue(value?.name) } : {}),
    ...(stringValue(value?.returnPath) ? { returnPath: stringValue(value?.returnPath) } : {}),
    ...(stringValue(value?.sessionId) ? { sessionId: stringValue(value?.sessionId) } : {}),
    ...(sessions.length ? { sessions } : {}),
  }
}

function openAnalystModel(value: SmartworkOpenAnalystModel | undefined) {
  const providerID = stringValue(value?.providerID)
  const modelID = stringValue(value?.modelID)
  const variant = typeof value?.variant === "string" ? stringValue(value.variant) : undefined
  if (!providerID || !modelID) return undefined
  return {
    providerID,
    modelID,
    ...(value?.variant === null ? { variant: null } : variant ? { variant } : {}),
    ...(stringValue(value?.label) ? { label: stringValue(value?.label) } : {}),
    ...(stringValue(value?.providerLabel) ? { providerLabel: stringValue(value?.providerLabel) } : {}),
  }
}

function openAnalystProviders(value: SmartworkOpenAnalystProviders | undefined) {
  if (!value) return undefined
  const all = value.all.map(provider).filter((item): item is SmartworkProvider => !!item)
  if (all.length === 0) return undefined
  const known = new Set(all.map((item) => item.id))
  return {
    all,
    connected: value.connected.map((item) => item.trim()).filter((item) => item && known.has(item)),
    default: Object.fromEntries(
      Object.entries(value.default)
        .map(([key, item]) => [key.trim(), item.trim()] as const)
        .filter(([key, item]) => key && item && known.has(key) && !!all.find((entry) => entry.id === key)?.models[item]),
    ),
  }
}

function openAnalystWorkspace(value: SmartworkOpenAnalystWorkspace | undefined) {
  if (!record(value) || value.schemaVersion !== 1) return undefined
  const displayIdentity = record(value.displayIdentity) ? value.displayIdentity : undefined
  const operationalIdentity = record(value.operationalIdentity) ? value.operationalIdentity : undefined
  if (!displayIdentity || !operationalIdentity) return undefined
  const title = optionalStringValue(displayIdentity.title)
  const source = displayIdentity.source === "cloud" || displayIdentity.source === "local" ? displayIdentity.source : undefined
  if (!title || !source) return undefined
  const namespace = optionalStringValue(displayIdentity.namespace)
  const subtitle = optionalStringValue(displayIdentity.subtitle)
  const logoUrl = httpBaseUrl(optionalStringValue(displayIdentity.logoUrl))
  const color =
    typeof displayIdentity.color === "string" && /^#[0-9a-fA-F]{6}$/.test(displayIdentity.color.trim())
      ? displayIdentity.color.trim()
      : undefined
  const cloudIdentity = record(value.cloud) ? value.cloud : undefined
  const capabilityKeys = normalizeStringList(cloudIdentity?.capabilityKeys)
  const cloud = cloudIdentity
    ? {
        ...(optionalStringValue(cloudIdentity.handle) ? { handle: optionalStringValue(cloudIdentity.handle) } : {}),
        ...(optionalStringValue(cloudIdentity.handleDisplay)
          ? { handleDisplay: optionalStringValue(cloudIdentity.handleDisplay) }
          : {}),
        ...(optionalStringValue(cloudIdentity.projectTitle)
          ? { projectTitle: optionalStringValue(cloudIdentity.projectTitle) }
          : {}),
        ...(optionalStringValue(cloudIdentity.updatedAt) ? { updatedAt: optionalStringValue(cloudIdentity.updatedAt) } : {}),
        ...(capabilityKeys ? { capabilityKeys } : {}),
      }
    : undefined
  const activityIdentity = record(value.activity) ? value.activity : undefined
  const activity = activityIdentity
    ? {
        ...(
          activityIdentity.latestKind === "agent" ||
          activityIdentity.latestKind === "automation" ||
          activityIdentity.latestKind === "message" ||
          activityIdentity.latestKind === "runtime"
            ? { latestKind: activityIdentity.latestKind }
            : {}
        ),
        ...(optionalStringValue(activityIdentity.label) ? { label: optionalStringValue(activityIdentity.label) } : {}),
        ...(optionalStringValue(activityIdentity.detail) ? { detail: optionalStringValue(activityIdentity.detail) } : {}),
        ...(optionalStringValue(activityIdentity.updatedAt)
          ? { updatedAt: optionalStringValue(activityIdentity.updatedAt) }
          : {}),
      }
    : undefined
  return {
    schemaVersion: 1,
    displayIdentity: {
      title,
      ...(namespace ? { namespace } : {}),
      ...(subtitle ? { subtitle } : {}),
      ...(logoUrl ? { logoUrl } : {}),
      ...(color ? { color } : {}),
      source,
    },
    operationalIdentity: {
      ...(optionalStringValue(operationalIdentity.desktopId)
        ? { desktopId: optionalStringValue(operationalIdentity.desktopId) }
        : {}),
      ...(optionalStringValue(operationalIdentity.smartworkWorkspaceId)
        ? { smartworkWorkspaceId: optionalStringValue(operationalIdentity.smartworkWorkspaceId) }
        : {}),
      ...(optionalStringValue(operationalIdentity.localRuntimeWorkspaceId)
        ? { localRuntimeWorkspaceId: optionalStringValue(operationalIdentity.localRuntimeWorkspaceId) }
        : {}),
      ...(optionalStringValue(operationalIdentity.openAnalystProjectSlug)
        ? { openAnalystProjectSlug: optionalStringValue(operationalIdentity.openAnalystProjectSlug) }
        : {}),
      ...(optionalStringValue(operationalIdentity.openAnalystProjectDirectory)
        ? {
            openAnalystProjectDirectory: optionalStringValue(operationalIdentity.openAnalystProjectDirectory),
          }
        : {}),
      ...(optionalStringValue(operationalIdentity.openAnalystProjectId)
        ? { openAnalystProjectId: optionalStringValue(operationalIdentity.openAnalystProjectId) }
        : {}),
    },
    ...(cloud && Object.keys(cloud).length ? { cloud } : {}),
    ...(activity && Object.keys(activity).length ? { activity } : {}),
  }
}

export function smartworkAppUrl(env: Env = currentEnv()) {
  return envString(env, "VITE_SMARTWORK_APP_URL") || SMARTWORK_DEFAULT_APP_URL
}

export function smartworkApiBaseUrl(env: Env = currentEnv()) {
  return smartworkUrl(env)
}

export function smartworkUrl(env: Env = currentEnv()) {
  return (
    smartworkBaseUrl(envString(env, "VITE_SMARTWORK_URL")) ||
    smartworkBaseUrl(envString(env, "VITE_SMARTWORK_API_BASE_URL")) ||
    openAnalystTenxBaseUrl(env)
  )
}

export function smartworkApiBaseUrlOverride(env: Env = currentEnv()) {
  return smartworkUrlOverride(env)
}

export function smartworkUrlOverride(env: Env = currentEnv()) {
  return (
    smartworkBaseUrl(envString(env, "VITE_SMARTWORK_URL")) ||
    smartworkBaseUrl(envString(env, "VITE_SMARTWORK_API_BASE_URL")) ||
    undefined
  )
}

export function legacySmartworkApiBaseUrlOverride(env: Env = currentEnv()) {
  return smartworkBaseUrl(envString(env, "VITE_SMARTWORK_API_BASE_URL")) || undefined
}

export function smartworkAppOrigin(env: Env = currentEnv()) {
  try {
    return new URL(smartworkAppUrl(env)).origin
  } catch {
    return null
  }
}

export function smartworkAppUserFacingOpenUrl(input: string = smartworkAppUrl()) {
  const trimmed = input.trim()
  try {
    const url = new URL(trimmed)
    const hostname = url.hostname.toLowerCase()
    if (url.protocol !== "http:" && url.protocol !== "https:") return null
    if (hostname === "localhost" || hostname === "::1" || hostname === "[::1]" || hostname.startsWith("127.")) {
      return null
    }
    return trimmed
  } catch {
    return null
  }
}

export function smartworkViewFromSearch(search: string): SmartworkView {
  const view = new URLSearchParams(search).get("view")
  if (
    view === "integrations" ||
    view === "skills" ||
    view === "plugins" ||
    view === "automations" ||
    view === "apps" ||
    view === "billing"
  )
    return view
  return "session"
}

export function smartworkProjectFromSearch(search: string) {
  const slug = new URLSearchParams(search).get("project")?.trim()
  if (!slug) return null
  try {
    return smartworkProjectRoute(base64Decode(slug))
  } catch {
    return null
  }
}

function normalizeSmartworkProjectDirectory(directory: string) {
  const match = directory.trim().replace(/\\/g, "/").match(/^\/workspace\/projects\/[a-f0-9]{24}(\/.+)$/i)
  return match ? `/workspace/projects${match[1]}` : directory
}

function smartworkProjectRoute(directory: string) {
  const normalized = normalizeSmartworkProjectDirectory(directory)
  if (!isSmartworkProjectDirectory(normalized)) return null
  return { slug: base64Encode(normalized), directory: normalized }
}

export function isSmartworkProjectDirectory(directory: string) {
  const value = directory.trim()
  if (!value) return false
  if (/^[\\/]+$/.test(value)) return false
  if (/^[A-Za-z]:[\\/]*$/.test(value)) return false
  return true
}

export function smartworkViewPath(view: SmartworkView, projectSlug?: string | null): string {
  const params = new URLSearchParams()
  if (view !== "session") params.set("view", view)
  if (projectSlug?.trim()) params.set("project", projectSlug.trim())
  const query = params.toString()
  return query ? `/smartwork?${query}` : "/smartwork"
}

export function smartworkCloudReviewPath(projectSlug?: string | null): string {
  const params = new URLSearchParams()
  if (projectSlug?.trim()) params.set("project", projectSlug.trim())
  params.set("reviewCloud", "1")
  return `/smartwork?${params.toString()}`
}

export function smartworkReviewCloudFromSearch(search: string) {
  return new URLSearchParams(search).get("reviewCloud") === "1"
}

export function smartworkReviewCloudConsumedPath(search: string) {
  const params = new URLSearchParams(search)
  params.delete("reviewCloud")
  params.delete("view")
  const query = params.toString()
  return query ? `/smartwork?${query}` : "/smartwork"
}

export function smartworkDashboardPath(currentDir?: string | null, projectSlug?: string | null): string {
  const dir = currentDir?.trim() || projectSlug?.trim()
  return dir ? pluginAppSessionHref({ dir, id: "dashboard" }) : "/"
}

export function smartworkFrameUrl(input: string, view: SmartworkView, nonce: number) {
  try {
    const url = new URL(input)
    url.pathname =
      view === "plugins"
        ? "/settings/extensions"
        : view === "automations"
          ? "/settings/automations"
          : view === "billing"
            ? "/settings/billing"
            : "/session"
    url.search = ""
    if (view === "plugins") url.searchParams.set("section", "plugins")
    url.searchParams.set("openanalyst_host", "1")
    url.searchParams.set("smartwork_reload", String(nonce))
    return url.toString()
  } catch {
    return input
  }
}

function storage(input?: SmartworkStorage) {
  if (input) return input
  if (typeof sessionStorage === "undefined") return null
  return sessionStorage
}

function validReturnPath(path: string) {
  const value = path.trim()
  if (!value.startsWith("/")) return false
  if (value.startsWith("//")) return false
  if (value === "/smartwork" || value.startsWith("/smartwork?") || value.startsWith("/smartwork/")) return false
  if (value === "/login" || value.startsWith("/login?") || value.startsWith("/login/")) return false
  if (value === "/callback" || value.startsWith("/callback?") || value.startsWith("/callback/")) return false
  if (value.startsWith("/auth/")) return false
  if (value.startsWith("/verify-email")) return false
  if (value.startsWith("/reset-password")) return false
  if (value.startsWith("/signup")) return false
  return true
}

export function readSmartworkReturnPath(input?: SmartworkStorage) {
  const target = storage(input)
  if (!target) return "/"
  try {
    const value = target.getItem(SMARTWORK_RETURN_PATH_STORAGE_KEY) ?? ""
    if (!validReturnPath(value)) return "/"
    return value
  } catch {
    return "/"
  }
}

export function writeSmartworkReturnPath(path: string, input?: SmartworkStorage) {
  if (!validReturnPath(path)) return false
  const target = storage(input)
  if (!target) return false
  try {
    target.setItem(SMARTWORK_RETURN_PATH_STORAGE_KEY, path)
    return true
  } catch {
    return false
  }
}

export function smartworkAppWorkspaceFromReturnPath(path: string) {
  const value = path.trim()
  if (!validReturnPath(value)) return null
  const slug = value.match(/^\/([^/?#]+)/)?.[1]
  if (!slug) return null

  try {
    return smartworkProjectRoute(base64Decode(slug))
  } catch {
    return null
  }
}

export function smartworkAppSessionFromReturnPath(path: string) {
  const value = path.trim()
  if (!validReturnPath(value)) return null
  return value.match(/^\/[^/?#]+\/session\/([^/?#]+)/)?.[1] ?? null
}

export function createSmartworkAuthMessage(input: {
  token?: string | null
  smartworkToken?: string | null
  user: AuthUser | null
  smartworkUrl?: string
  smartworkApiBaseUrl?: string
  localRuntimeUrl?: string
  localRuntimeWorkspaceId?: string
  smartworkRuntimeUrl?: string
  smartworkWorkspaceId?: string
  smartworkHandle?: string
  openAnalystProject?: SmartworkOpenAnalystProject
  openAnalystModel?: SmartworkOpenAnalystModel
  openAnalystProviders?: SmartworkOpenAnalystProviders
  openAnalystWorkspace?: SmartworkOpenAnalystWorkspace
  now?: () => number
}) {
  const smartworkToken = stringValue(input.smartworkToken ?? input.token ?? undefined)
  const localRuntimeUrl = httpBaseUrl(input.localRuntimeUrl ?? input.smartworkRuntimeUrl)
  const localRuntimeWorkspaceId = stringValue(input.localRuntimeWorkspaceId ?? input.smartworkWorkspaceId)
  const smartworkHandle = stringValue(input.smartworkHandle)
  const project = openAnalystProject(input.openAnalystProject)
  const model = openAnalystModel(input.openAnalystModel)
  const providers = openAnalystProviders(input.openAnalystProviders)
  const workspace = openAnalystWorkspace(input.openAnalystWorkspace)
  const url = smartworkBaseUrl(input.smartworkUrl ?? input.smartworkApiBaseUrl ?? "") || smartworkUrl()

  return {
    type: SMARTWORK_AUTH_MESSAGE_TYPE,
    ...(smartworkToken ? { smartworkToken, token: smartworkToken } : {}),
    user: input.user
      ? {
          userId: input.user.userId,
          email: input.user.email,
          fullName: input.user.fullName,
          orgId: input.user.orgId,
        }
      : null,
    smartworkUrl: url,
    ...(localRuntimeUrl ? { localRuntimeUrl } : {}),
    ...(localRuntimeWorkspaceId ? { localRuntimeWorkspaceId } : {}),
    ...(smartworkHandle ? { smartworkHandle } : {}),
    ...(project ? { openAnalystProject: project } : {}),
    ...(model ? { openAnalystModel: model } : {}),
    ...(providers ? { openAnalystProviders: providers } : {}),
    ...(workspace ? { openAnalystWorkspace: workspace } : {}),
    issuedAt: input.now?.() ?? Date.now(),
  }
}

export function createSmartworkLogoutMessage() {
  return { type: SMARTWORK_LOGOUT_MESSAGE_TYPE }
}

export function readSmartworkPickDirectoryRequest(value: unknown) {
  if (!record(value)) return null
  if (value.type !== SMARTWORK_PICK_DIRECTORY_REQUEST_MESSAGE_TYPE) return null
  if (typeof value.id !== "string" || !value.id.trim()) return null
  return {
    id: value.id,
    title: typeof value.title === "string" && value.title.trim() ? value.title : undefined,
  }
}

export function createSmartworkPickDirectoryResponse(input: {
  id: string
  path?: string | null
  error?: string | null
}) {
  return {
    type: SMARTWORK_PICK_DIRECTORY_RESPONSE_MESSAGE_TYPE,
    id: input.id,
    path: input.path?.trim() || null,
    error: input.error?.trim() || null,
  }
}
